import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DefaultLocalOrderService } from '@asm/features/sync/services/orders/default-local-order.service';
import { DefaultLocalOrderHistoryService } from '@asm/features/sync/services/orders/default-local-order-history.service';
import { CountryOriginRepository } from '@asm/features/sync/repositories/country-origin.repository';
import { DocumentTypeRepository } from '@asm/features/sync/repositories/document-type.repository';
import { FreightTypeRepository } from '@asm/features/sync/repositories/freight-type.repository';
import { StorageConditionRepository } from '@asm/features/sync/repositories/storage-condition.repository';
import { normalizeBoolean } from '@asm/features/sync/services/sync/sync.normalizers';
import { firstValueFrom } from 'rxjs';
import { OfflineOrderOutboxRepository } from '@asm/features/sync/repositories/offline-order-outbox.repository';

/**
 * Handler offline para operações de pedido — atua como controller HTTP.
 * Extrai os dados do request e delega toda a lógica ao OfflineOrderValidationService.
 *
 * Cobre: POST /order/place-order, POST /mix/calculate-adherence,
 *        POST /order/{uid}/{uid}/{orderCode}/copy-to-cart,
 *        GET  /order/history/{uid}/{uid}/{orderCode},
 *        GET  /order/history/{uid}/{uid}
 */
/** Regex para detalhe: /order/history/{uid}/{uid}/{orderCode} — 3 segmentos */
const ORDER_HISTORY_DETAIL_RE = /\/order\/history\/([^/]+)\/([^/]+)\/([^/]+)$/;
/** Regex para lista:   /order/history/{uid}/{uid}             — 2 segmentos */
const ORDER_HISTORY_LIST_RE = /\/order\/history\/([^/]+)\/([^/]+)$/;
/** Regex para copy-to-cart: /order/{uid}/{uid}/{orderCode}/copy-to-cart */
const ORDER_COPY_TO_CART_RE = /\/order\/([^/]+)\/([^/]+)\/([^/]+)\/copy-to-cart$/;
/** Regex para detalhe por pedidos: /order/pedidos/{orderCode} */
const ORDER_PEDIDOS_DETAIL_RE = /\/order\/pedidos\/([^/]+)$/;
/** Regex para histórico de preços: /order/price-history/{uid}/{uid}/{productCode} */
const ORDER_PRICE_HISTORY_RE = /\/order\/price-history\/([^/]+)\/([^/]+)\/([^/]+)$/;

@Injectable({ providedIn: 'root' })
export class OrderOfflineHandler {
  private readonly orderService = inject(DefaultLocalOrderService);
  private readonly orderHistoryService = inject(DefaultLocalOrderHistoryService);
  private readonly outboxRepo = inject(OfflineOrderOutboxRepository);
  private readonly documentTypeRepo = inject(DocumentTypeRepository);
  private readonly freightTypeRepo = inject(FreightTypeRepository);
  private readonly storageConditionRepo = inject(StorageConditionRepository);
  private readonly countryOriginRepo = inject(CountryOriginRepository);

  canHandle(req: HttpRequest<unknown>): boolean {
    const { url, method } = req;
    return (
      (method === 'POST' && url.includes('/order/place-order')) ||
      (method === 'POST' && url.includes('/mix/calculate-adherence')) ||
      (method === 'POST' && ORDER_COPY_TO_CART_RE.test(url)) ||
      (method === 'GET' &&
        (url.includes('/document-type/select') ||
          /\/document-type\/[^/]+$/.test(url) ||
          url.includes('/freight-type/select') ||
          url.includes('/storage-condition/select') ||
          url.includes('/country-origin/select') ||
          url.includes('/order/reference-select'))) ||
      // Histórico de preços do produto — testar ANTES do history-detail (mais específico)
      (method === 'GET' && ORDER_PRICE_HISTORY_RE.test(url)) ||
      // Detalhe do pedido — testar ANTES da lista (regex mais específico)
      (method === 'GET' && ORDER_HISTORY_DETAIL_RE.test(url)) ||
      // Listagem do histórico
      (method === 'GET' && ORDER_HISTORY_LIST_RE.test(url)) ||
      // Detalhe via outbox (clientOrderId)
      (method === 'GET' && ORDER_PEDIDOS_DETAIL_RE.test(url))
    );
  }

  async handle(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const { url, method } = req;

    if (method === 'POST' && url.includes('/mix/calculate-adherence')) {
      return new HttpResponse({ status: 200, body: [] });
    }

    if (method === 'POST' && url.includes('/order/place-order')) {
      const cartId = req.headers.get('x-cart-id') ?? '';
      const b2bUnitCode = req.headers.get('x-b2bunit-id') ?? '';
      const customerId = req.headers.get('x-b2bcustomer-id') ?? '';

      const result = await this.orderService.placeOrder(cartId, b2bUnitCode, customerId);
      return new HttpResponse({ status: 200, body: result });
    }

    if (method === 'GET' && url.includes('/order/reference-select')) {
      const desiredDeliveryDate = req.params.get('desiredDeliveryDate') ?? '';
      const unitUid = req.headers.get('x-b2bunit-id') ?? '';
      const customerUid = req.headers.get('x-b2bcustomer-id') ?? '';
      return new HttpResponse({
        status: 200,
        body: await this.orderService.listReferenceOrders(desiredDeliveryDate, unitUid, customerUid),
      });
    }

    if (url.includes('/document-type/select')) {
      return new HttpResponse({ status: 200, body: await firstValueFrom(this.documentTypeRepo.getAll()) });
    }

    if (/\/document-type\/[^/]+$/.test(url)) {
      const code = url.split('/document-type/')[1];
      const items = await firstValueFrom(this.documentTypeRepo.getAll());
      const item = items.find(it => it.code === code) ?? null;
      if (!item) return new HttpResponse({ status: 200, body: null });
      return new HttpResponse({
        status: 200,
        body: {
          ...item,
          requireClientInvoice: normalizeBoolean((item as any).requireClientInvoice),
          isQuotation: normalizeBoolean((item as any).isQuotation),
        },
      });
    }

    if (url.includes('/freight-type/select')) {
      return new HttpResponse({ status: 200, body: await firstValueFrom(this.freightTypeRepo.getAll()) });
    }

    if (url.includes('/storage-condition/select')) {
      return new HttpResponse({ status: 200, body: await firstValueFrom(this.storageConditionRepo.getAll()) });
    }

    if (url.includes('/country-origin/select')) {
      return new HttpResponse({ status: 200, body: await firstValueFrom(this.countryOriginRepo.getAll()) });
    }

    // ── Copy to cart ──────────────────────────────────────────────────────
    const copyMatch = url.match(ORDER_COPY_TO_CART_RE);
    if (method === 'POST' && copyMatch) {
      const [, b2bUnitUid, customerUid, orderCode] = copyMatch;
      const body = await this.orderService.copyOrderToCart(b2bUnitUid, customerUid, orderCode);
      return new HttpResponse({ status: 200, body });
    }

    // ── Detalhe via outbox (clientOrderId para /order/pedidos/:code) ──────────
    const pedidosMatch = url.match(ORDER_PEDIDOS_DETAIL_RE);
    if (method === 'GET' && pedidosMatch) {
      const [, clientOrderId] = pedidosMatch;
      const b2bUnitUid = req.headers.get('x-b2bunit-id') ?? '';
      const customerUid = req.headers.get('x-b2bcustomer-id') ?? '';
      const body = await this.orderHistoryService.getDetailFromOutbox(b2bUnitUid, customerUid, clientOrderId);
      return new HttpResponse({ status: body ? 200 : 404, body });
    }

    // ── Detalhe do pedido ──
    const detailMatch = url.match(ORDER_HISTORY_DETAIL_RE);
    if (method === 'GET' && detailMatch) {
      const [, b2bUnitUid, customerUid, orderCode] = detailMatch;
      const body = await this.orderHistoryService.getDetail(b2bUnitUid, customerUid, orderCode);
      return new HttpResponse({ status: body ? 200 : 404, body });
    }

    // ── Listagem do histórico  ──────────────────────────────────
    const listMatch = url.match(ORDER_HISTORY_LIST_RE);
    if (method === 'GET' && listMatch) {
      const [, b2bUnitUid, customerUid] = listMatch;
      const type = req.params.get('type') ?? 'orders';
      // Connector sends 1-based page numbers (to match the online API); convert to 0-based for the local service.
      const page = Math.max(0, Number(req.params.get('page') ?? '1') - 1);
      const pageSize = Number(req.params.get('pageSize') ?? '10');
      const search = req.params.get('search') ?? '';
      const body = await this.orderHistoryService.list(b2bUnitUid, customerUid, { type, page, pageSize, search });
      return new HttpResponse({ status: 200, body });
    }

    // ── Histórico de preços do produto ────────────────────────────────────────
    const priceHistoryMatch = url.match(ORDER_PRICE_HISTORY_RE);
    if (method === 'GET' && priceHistoryMatch) {
      const [, b2bUnitUid, customerUid, productCode] = priceHistoryMatch;
      const page = Math.max(0, Number(req.params.get('page') ?? '0'));
      const pageSize = Number(req.params.get('pageSize') ?? '10');
      const body = await this.orderHistoryService.getProductPriceHistory(
        b2bUnitUid,
        customerUid,
        decodeURIComponent(productCode),
        page,
        pageSize,
      );
      return new HttpResponse({ status: 200, body });
    }

    throw new Error(`[OrderOfflineHandler] Rota não mapeada: ${method} ${url}`);
  }
}
